import { useGetBudgetsByVesselId } from "@/services/queries/budgets";
import { useGetCurrencies } from "@/services/queries/currency";
import { useGetVendors } from "@/services/queries/vendor";
import { useGetVessels } from "@/services/queries/vessel";
import React, { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Step } from "./constants";
import InitialStep from "./initial-step";
import BudgetStep from "./budget-step";
import VendorStep from "./vendor-step";
import CurrencyStep from "./currency-step";
import NoteStep from "./note-step";
import CostCenter from "./cost-center";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import LineItemsForm from "./line-items-form";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { ApiResponse, BudgetSubCategory } from "@/lib/types";
import { BudgetCategory } from "@/lib/types";
import { api } from "@/lib/api";
import InvoiceNumberStep from "./invoice-number";
import StatusStep from "./status-step";
import VendorPaymentDetailsStep, {
  VendorPaymentDetailsFormValues,
} from "@/pages/main/components/add-vendor-payment-details";
import { useConversionRate } from "@/hooks/use-conversion-rate";
import { toDateOnlyString } from "@/lib/utils";

export type InvoiceFormValues = {
  totalAmount: number;
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;

  currencyId: string;
  conversionRate?: number;
  description: string;
  reference: string;
  vesselId: string;
  budgetId: string;
  files: { id: string; url: string }[] | File[];
  status: string;
  lineItems: {
    id?: string | null;
    description: string;
    amount: number;
    quantity: number;
    unitPrice: number;
    vatRate: number;
    vatAmount: number;
    budgetCategoryId?: string | null;
    budgetSubcategoryId?: string | null;
    budgetLineItemId?: string | null;
  }[];
  vendor: string | { id: string } | null;
  vendorPaymentMethods?: {
    iban: string;
    bankName: string;
    swiftCode: string;
    accountHolderName: string;
    currencyId: string;
    bankAddress: string;
    bankAccountNumber: string;
    paymentTerms: string;
    isDefault: boolean;
  }[];
};

export const invoiceFormSchema = z.object({
  issueDate: z.date(),
  dueDate: z.date(),
  vendor: z
    .string()
    .or(z.object({ id: z.string().min(1) }))
    .or(z.null())
    .refine((value) => {
      if (value && typeof value === "object" && "id" in value) {
        return value.id.length > 0;
      } else if (value && typeof value === "string") {
        return value.length > 0;
      }
      return false;
    }),
  vendorPaymentMethods: z.array(
    z.object({
      iban: z.string().optional(),
      bankName: z.string().optional(),
      swiftCode: z.string().optional(),
      accountHolderName: z.string().optional(),
      currencyId: z.string().optional(),
      bankAddress: z.string().optional(),
      bankAccountNumber: z.string().optional(),
      paymentTerms: z.string().optional(),
      isDefault: z.boolean().optional(),
    })
  ),
  currencyId: z.string().min(1),
  conversionRate: z.number().optional(),
  status: z.string().min(1),
  description: z.string().optional(),
  reference: z.string().optional(),
  invoiceNumber: z.string().min(1),
  vesselId: z.string().min(1),
  budgetId: z.string().min(1),
  files: z.array(z.any()).optional(),
  lineItems: z.array(
    z.object({
      description: z.string().optional(),
      amount: z.number().optional(),
      quantity: z.number(),
      unitPrice: z.number(),
      vatRate: z.number(),
      vatAmount: z.number().optional(),
      budgetSubcategoryId: z.string().or(z.null()).optional(),
    })
  ),
});

const InvoiceForm = ({
  title,
  open,
  setOpen,
  form,
  step,
  setStep,
  defaultValues,
  onSubmit,
  isSubmitting,
  initialVesselId,
}: {
  title?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  form: UseFormReturn<InvoiceFormValues>;
  defaultValues: Partial<InvoiceFormValues>;
  onSubmit: (values: InvoiceFormValues) => void;
  /** True while a create/update-invoice (or its vendor) request is in flight; disables Save to prevent duplicate submissions. */
  isSubmitting?: boolean;
  initialVesselId?: string;
}) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<
    (BudgetCategory & { subCategories?: BudgetSubCategory[] })[]
  >([]);
  const { data: currenciesData, isPending: isCurrenciesPending } =
    useGetCurrencies();
  const { data: vendorsData, isPending: isVendorsPending } = useGetVendors();
  const { data: vesselsData } = useGetVessels();
  const { data: budgetsData } = useGetBudgetsByVesselId(form.watch("vesselId"));
  const selectedIssueDate = form.watch("issueDate") ?? new Date();
  const selectedCurrencyId = form.watch("currencyId");
  const selectedVesselId = form.watch("vesselId");

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const vendors = useMemo(() => {
    return vendorsData?.value ?? [];
  }, [vendorsData?.value]);

  const vessels = useMemo(() => {
    return vesselsData?.value ?? [];
  }, [vesselsData?.value]);

  const selectedVessel = useMemo(
    () => vessels.find((vessel) => vessel.id === selectedVesselId),
    [vessels, selectedVesselId]
  );

  const budgets = useMemo(() => {
    return budgetsData?.value ?? [];
  }, [budgetsData?.value]);

  // BE requires `conversionRate` whenever invoice currency differs from the
  // vessel's default currency, and historical accuracy matters: the rate must
  // be the one effective on the invoice's issue date (or the closest available),
  // not today's rate.
  const { rate: conversionRate } = useConversionRate({
    fromCurrencyId: selectedCurrencyId,
    toCurrencyId: selectedVessel?.defaultCurrencyId,
    date: selectedIssueDate ? toDateOnlyString(selectedIssueDate) : undefined,
    initialRate: defaultValues?.conversionRate ?? undefined,
  });

  // Sync derived rate to RHF for submit. There's no visible rate input on the
  // invoice form today, so we don't need to expose setRate to children — the
  // hook's auto-fetch is the only source after hydration.
  useEffect(() => {
    form.setValue("conversionRate", conversionRate);
  }, [conversionRate, form]);

  const getStep = () => {
    switch (step) {
      case Step.INITAL:
        return (
          <InitialStep
            title={title}
            form={form}
            setStep={setStep}
            currencies={currencies}
            vessels={vessels}
            budgets={budgets}
            vendors={vendors}
            isSubmitting={isSubmitting}
          />
        );
      case Step.BUDGET:
        return <BudgetStep form={form} setStep={setStep} />;
      case Step.VENDOR:
        return (
          <VendorStep
            form={form}
            setStep={setStep}
            vendors={vendors}
            isPending={isVendorsPending}
          />
        );
      case Step.CURRENCY:
        return (
          <CurrencyStep
            isCurrenciesPending={isCurrenciesPending}
            currencies={currencies}
            form={form}
            setStep={setStep}
            vessels={vessels}
          />
        );
      case Step.NOTE:
        return <NoteStep form={form} setStep={setStep} />;
      case Step.COST_CENTER:
        return <CostCenter form={form} setStep={setStep} />;
      case Step.INVOICE_NUMBER:
        return <InvoiceNumberStep form={form} setStep={setStep} />;
      case Step.STATUS:
        return <StatusStep form={form} setStep={setStep} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    // Set the cost center to the selected boat
    if (initialVesselId) {
      form.setValue("vesselId", initialVesselId);
    }
  }, [form, initialVesselId]);

  const budgetId = form.watch("budgetId");

  useEffect(() => {
    const getCategories = (budgetId?: string) => {
      if (!budgetId) return;

      return api.get<ApiResponse<BudgetCategory[]>>(
        `budgets/${budgetId}/categories`
      );
    };

    const getSubCategories = (categoryId?: string) => {
      if (!categoryId) return;

      return api.get<ApiResponse<BudgetSubCategory[]>>(
        `budgets/categories/${categoryId}/subcategories`
      );
    };

    const getCategoriesForAllBudgets = async () => {
      const allCategories: (BudgetCategory & {
        subCategories?: BudgetSubCategory[];
      })[] = [];

      const response = await getCategories(budgetId);

      if (response && response.data) {
        const categoriesArray = response.data.value;
        allCategories.push(...categoriesArray);
      }

      const subCategoriesPromises = allCategories.map(async (category) => {
        const response = await getSubCategories(category.id);
        if (response && response.data) {
          category.subCategories = response.data.value;
          return category.subCategories.length > 0 ? category : null;
        }
        return null;
      });

      const filteredCategories = (
        await Promise.all(subCategoriesPromises)
      ).filter(Boolean);
      setCategories(
        filteredCategories as (BudgetCategory & {
          subCategories?: BudgetSubCategory[];
        })[]
      );
    };

    getCategoriesForAllBudgets();
  }, [budgetId]);

  return (
    <Drawer
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setStep(Step.INITAL);
          form.reset({
            ...defaultValues,
          });
        }
      }}
    >
      <DrawerContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        data-testid="invoice-form-drawer"
      >
        <DrawerHeader className="hidden">
          <DrawerTitle>{title ?? t("title.add_invoice")}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription />
        <Form {...form}>
          <form
            data-testid="invoice-form"
            onSubmit={(e) => {
              form.handleSubmit(onSubmit, (errors) => {
                console.warn(errors);
              })(e);
            }}
          >
            {getStep()}
          </form>
        </Form>

        {step === Step.INITAL && (
          <LineItemsForm
            currencies={currencies}
            invoiceForm={form}
            categories={categories}
          />
        )}

        {/* It should be outside of the drawer form caused of its inside form */}
        {step === Step.VENDOR_PAYMENT_DETAILS && (
          <VendorPaymentDetailsStep
            form={form as UseFormReturn<VendorPaymentDetailsFormValues>}
            setStep={setStep}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default InvoiceForm;
